import { Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogContentText, Grid, TextField, Autocomplete, FormControlLabel, Switch, AppBar, Toolbar, Typography, debounce, IconButton, InputAdornment, Chip, Box } from "@mui/material"
import { useAppDispatch, useAppSelector } from "../../../../../hooks/store-hooks"
import { useUsuariosService } from "../../../../../../use-cases/usuarios"
import CloseIcon from '@mui/icons-material/Close';
import { ITipoUsuario, tipoUsuarioOptions, tipoUsuarioToLabel } from "../../../../../../application/models";
import { useEffect, useRef, useState } from "react";
import { useUIService } from "../../../../../../use-cases/ui";
import { useNavigationService } from "../../../../../../use-cases/navigation";
import { setCreateUsuarioFormIdsClientesState, setCreateUsuarioFormBuscaDisponiveisState, setCreateUsuarioFormBuscaSelecionadosState } from "../../../../../../application/states/usuarios";
import { VisualizacaoClientesAccordion } from './VisualizacaoClientesAccordion';

export const FormCreateUsuario = () => {
  // State
  const { createUsuarioForm, clienteSelecionado, clientesVendedorVeles } = useAppSelector((state) => state.usuarios)
  const { perfis } = useAppSelector((state) => state.perfil)
  const { currentUser } = useAppSelector((state) => state.user)
  const { idVelesCliente } = useAppSelector((state) => state.auth)
  const targetVelesClienteId = idVelesCliente
  const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS
  const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO
  const optionsTipoUsuario = isTecnologiaDaInformacao
    ? tipoUsuarioOptions
    : tipoUsuarioOptions.filter(opt => opt !== ITipoUsuario.TECNOLOGIA_DA_INFORMACAO)
  // Use State
  const [loading, setLoading] = useState<boolean>(true)
  // Campos de busca via state global
  const buscaDisponiveis = createUsuarioForm.fields.buscaDisponiveis || ''
  const buscaSelecionados = createUsuarioForm.fields.buscaSelecionados || ''
  // Use Ref
  const isFirstRender = useRef(false)
  // Service
  const dispatch = useAppDispatch()
  const {
    setOpenCreateUsuarioForm,
    setCreateUsuarioFormEmail,
    setCreateUsuarioFormNome,
    createUsuario,
    setCreateUsuarioFormIdPerfil,
    setCreateUsuarioFormTipoUsuario,
    setCreateUsuarioFormLogin,
    setCreateUsuarioFormTokenAtivo,
    getClientesVendedorVeles
  } = useUsuariosService()
  const { setAlert } = useUIService()
  const { navigateTo } = useNavigationService()
  // useEffect
  useEffect(() => {
    if (!isFirstRender.current) {
      setLoading(false)
      isFirstRender.current = true

      const isVelesCliente = clienteSelecionado?.IdCliente === targetVelesClienteId
      if ((isDemais || isTecnologiaDaInformacao) && isVelesCliente) {
        getClientesVendedorVeles()
      }

      if (!createUsuarioForm.fields.idCliente || createUsuarioForm.fields.idCliente === 0) {
        setAlert({
          message: 'Você deve selecionar um cliente',
          type: 'error'
        })

        navigateTo({
          route: '/dashboard/cadastro/usuario'
        })
      }
    }
  }, [createUsuarioForm.fields, navigateTo, setAlert, isDemais, isTecnologiaDaInformacao, getClientesVendedorVeles, dispatch, clienteSelecionado?.IdCliente, idVelesCliente])

  // Function
  const handleClose = () => {
    setOpenCreateUsuarioForm({
      openForm: false
    })
  }

  return (
    loading ? <></>
      : <>
        <Dialog
          open={true}
          fullScreen
          disableEscapeKeyDown
          onClose={handleClose}
          scroll={'paper'}
          aria-labelledby="scroll-dialog-title"
          aria-describedby="scroll-dialog-description"
          PaperProps={{
            component: 'form',
            onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              createUsuario();
            },
          }}
        >
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar>
              <Typography sx={{ ml: 2, flex: 1 }} textTransform={"uppercase"} fontWeight={"bold"} variant="h6" component="div">
                Criar Usuário
              </Typography>
              <Button
                color="inherit"
                startIcon={<CloseIcon />}
                onClick={handleClose}
                aria-label="close"
              >
                Fechar
              </Button>
            </Toolbar>
          </AppBar>
          <DialogContent dividers={true}>
            <DialogContentText id="scroll-dialog-description" tabIndex={-1}></DialogContentText>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  defaultValue={createUsuarioForm.fields.nome}
                  onChange={debounce((e) => {
                    setCreateUsuarioFormNome({
                      nome: e.target.value
                    })
                  }, 500)}
                  error={Boolean(createUsuarioForm.touched && createUsuarioForm.errors.find(item => item.key === 'nome'))}
                  helperText={createUsuarioForm.errors.find(item => item.key === 'nome')?.message}
                  name="nome"
                  fullWidth
                  id="nome"
                  label="Nome"
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  defaultValue={createUsuarioForm.fields.email}
                  onChange={debounce((e) => {
                    setCreateUsuarioFormEmail({
                      email: e.target.value
                    })
                  }, 500)}
                  error={Boolean(createUsuarioForm.touched && createUsuarioForm.errors.find(item => item.key === 'email'))}
                  helperText={createUsuarioForm.errors.find(item => item.key === 'email')?.message}
                  name="email"
                  fullWidth
                  id="email"
                  label="E-mail"
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  defaultValue={createUsuarioForm.fields.login}
                  onChange={debounce((e) => {
                    setCreateUsuarioFormLogin({
                      login: e.target.value
                    })
                  }, 500)}
                  error={Boolean(createUsuarioForm.touched && createUsuarioForm.errors.find(item => item.key === 'login'))}
                  helperText={createUsuarioForm.errors.find(item => item.key === 'login')?.message}
                  name="login"
                  fullWidth
                  id="login"
                  label="Login"
                  autoFocus
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  options={(() => {
                    // Filtrar perfis ativos e remover duplicatas por IdPerfil
                    const perfisAtivos = perfis.filter(p => p.Ativo);
                    
                    // Remover duplicatas baseado em IdPerfil
                    const perfisUnicos = perfisAtivos.filter((perfil, index, self) =>
                      index === self.findIndex(p => p.IdPerfil === perfil.IdPerfil)
                    );
                    
                    return perfisUnicos;
                  })()}
                  getOptionLabel={(option) => option.Descricao}
                  isOptionEqualToValue={(option, value) => option.IdPerfil === value.IdPerfil}
                  renderOption={(props, option) => (
                    <li {...props} key={option.IdPerfil}>
                      {option.Descricao}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Perfil"
                      value={createUsuarioForm.fields.idPerfil ? perfis.find((perfil) => perfil.IdPerfil === createUsuarioForm.fields.idPerfil)?.Descricao : ''}
                      fullWidth
                      error={Boolean(createUsuarioForm.touched && createUsuarioForm.errors.find(item => item.key === 'idPerfil'))}
                      helperText={createUsuarioForm.errors.find(item => item.key === 'idPerfil')?.message}
                    />
                  )}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setCreateUsuarioFormIdPerfil({
                        idPerfil: newValue.IdPerfil
                      })
                    }
                  }}
                  value={perfis.find((perfil) => perfil.IdPerfil === createUsuarioForm.fields.idPerfil)}
                />
              </Grid>
              {
                currentUser?.IdVelesPerfilInterno !== ITipoUsuario.USUARIO_CLIENTE && clienteSelecionado?.IdCliente === currentUser?.IdCliente && (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={optionsTipoUsuario}
                      getOptionLabel={(option) => tipoUsuarioToLabel(option).toUpperCase()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tipo de Usuário"
                          value={createUsuarioForm.fields.tipoUsuario ? tipoUsuarioToLabel(tipoUsuarioOptions.find((tu) => tu === createUsuarioForm.fields.tipoUsuario)!).toUpperCase() : ''}
                          fullWidth
                          error={Boolean(createUsuarioForm.touched && createUsuarioForm.errors.find(item => item.key === 'tipoUsuario'))}
                          helperText={createUsuarioForm.errors.find(item => item.key === 'tipoUsuario')?.message}
                        />
                      )}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setCreateUsuarioFormTipoUsuario({
                            tipoUsuario: newValue
                          })
                        }
                      }}
                      value={tipoUsuarioOptions.find((tu) => tu === createUsuarioForm.fields.tipoUsuario)}
                    />
                  </Grid>
                )
              }
              {(isDemais || isTecnologiaDaInformacao) && (clienteSelecionado?.IdCliente === targetVelesClienteId) && (
                <Grid item xs={12}>
                  <VisualizacaoClientesAccordion
                    idsClientes={createUsuarioForm.fields.idsClientes || []}
                    buscaDisponiveis={buscaDisponiveis}
                    buscaSelecionados={buscaSelecionados}
                    clientesVendedorVeles={clientesVendedorVeles}
                    canEdit={true}
                    onAddCliente={(idCliente) =>
                      dispatch(setCreateUsuarioFormIdsClientesState({
                        idsClientes: [...(createUsuarioForm.fields.idsClientes || []), idCliente]
                      }))
                    }
                    onRemoveCliente={(idCliente) =>
                      dispatch(setCreateUsuarioFormIdsClientesState({
                        idsClientes: (createUsuarioForm.fields.idsClientes || []).filter(id => id !== idCliente)
                      }))
                    }
                    onLimparTodos={() =>
                      dispatch(setCreateUsuarioFormIdsClientesState({ idsClientes: [] }))
                    }
                    onBuscaDisponiveisChange={(value) =>
                      dispatch(setCreateUsuarioFormBuscaDisponiveisState({ buscaDisponiveis: value }))
                    }
                    onBuscaSelecionadosChange={(value) =>
                      dispatch(setCreateUsuarioFormBuscaSelecionadosState({ buscaSelecionados: value }))
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ overflow: 'visible' }}>
                  <CardHeader
                    title="Token de API"
                    subheader="Ative o token para permitir integração via API após criar o usuário."
                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                    subheaderTypographyProps={{ variant: 'body2' }}
                  />
                  <CardContent sx={{ pt: 0 }}>
                    <FormControlLabel
                      control={<Switch
                        checked={createUsuarioForm.fields.tokenAtivo || false}
                        onChange={(e) => {
                          setCreateUsuarioFormTokenAtivo({
                            tokenAtivo: e.target.checked
                          })
                        }}
                        name="tokenAtivo"
                        color="primary"
                      />
                      }
                      label="Token Ativo"
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button type="submit" variant="contained">Criar Usuário</Button>
          </DialogActions>
        </Dialog>
      </>
  )
}